import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { Loader2, Mail, MessageSquare, CheckCircle2, ShieldCheck, Sparkles } from "lucide-react";

const CATEGORIES = [
  { value: "general", label: "General question" },
  { value: "seller", label: "Seller help" },
  { value: "buyer", label: "Buyer help" },
  { value: "partnership", label: "Partnership / press" },
  { value: "bug", label: "Bug report" },
];

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    category: "general",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null); // {enquiry_id} once submitted

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: typeof e === "string" ? e : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 4) {
      toast.error("Please add your name, email and a short message.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/contact", form);
      setSent(data);
      toast.success("Message sent — we'll be in touch soon.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your message. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[70vh] bg-[#FAF8F5]">
      <SEO
        title="Contact AfriMarket — get in touch with our team"
        description="Questions, partnerships, press, bug reports? Send us a message and the AfriMarket team will get back to you."
        canonical="/contact"
      />
      <div className="container-narrow py-12" data-testid="contact-page">
        <div className="grid lg:grid-cols-5 gap-10 items-start">
          {/* Left rail — context */}
          <div className="lg:col-span-2">
            <div className="label-overline text-[#C05A3C] mb-2">Get in touch</div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.05]">
              Talk to the AfriMarket team
            </h1>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              Sellers, buyers, journalists, partners — drop us a line. Real humans read every message
              and reply within <strong>one working day</strong> (Mon–Fri, UK time).
            </p>

            <div className="mt-8 space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-[#C05A3C]/10 text-[#C05A3C] flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-semibold text-sm">Order or payment issue?</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Open a free dispute in the{" "}
                    <a href="/complaints" className="text-[#C05A3C] underline">Resolution Centre</a>{" "}
                    so we can mediate with the seller.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-[#2A3D36]/10 text-[#2A3D36] flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-semibold text-sm">Partnership or press?</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Pick the &quot;Partnership / press&quot; category and one of the founders will reply directly.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-[#D9923B]/15 text-[#D9923B] flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-semibold text-sm">Prefer email?</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Write to <a href="mailto:support@afrimarket.org" className="text-[#C05A3C] underline">support@afrimarket.org</a> — the form below routes to the same inbox.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right card — the form */}
          <div className="lg:col-span-3">
            <div className="bg-white border border-black/10 rounded-2xl p-6 sm:p-8 shadow-sm">
              {sent ? (
                <div className="text-center py-10" data-testid="contact-sent">
                  <div className="w-14 h-14 rounded-full bg-[#2A3D36]/10 text-[#2A3D36] flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-7 h-7" />
                  </div>
                  <h2 className="font-display text-2xl mb-2">Thanks — your message is on its way ✨</h2>
                  <p className="text-muted-foreground text-sm leading-relaxed max-w-md mx-auto">
                    We&apos;ll reply to <strong>{form.email}</strong> within one working day.
                    Your reference is <span className="font-mono text-xs">{sent.enquiry_id}</span>.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    className="mt-6"
                    data-testid="contact-send-another"
                    onClick={() => {
                      setSent(null);
                      setForm({ name: "", email: "", subject: "", category: "general", message: "" });
                    }}
                  >
                    Send another message
                  </Button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#C05A3C]">
                    <MessageSquare className="w-3.5 h-3.5" /> New message
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="contact-name">Your name *</Label>
                      <Input
                        id="contact-name"
                        data-testid="contact-name"
                        value={form.name}
                        onChange={set("name")}
                        placeholder="Amara O."
                        required
                        maxLength={120}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="contact-email">Email *</Label>
                      <Input
                        id="contact-email"
                        data-testid="contact-email"
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        placeholder="you@example.com"
                        required
                        maxLength={200}
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="contact-category">Category</Label>
                      <Select value={form.category} onValueChange={(v) => setForm((s) => ({ ...s, category: v }))}>
                        <SelectTrigger id="contact-category" data-testid="contact-category">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map((c) => (
                            <SelectItem key={c.value} value={c.value} data-testid={`contact-category-${c.value}`}>
                              {c.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="contact-subject">Subject</Label>
                      <Input
                        id="contact-subject"
                        data-testid="contact-subject"
                        value={form.subject}
                        onChange={set("subject")}
                        placeholder="What's it about?"
                        maxLength={200}
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="contact-message">Message *</Label>
                    <Textarea
                      id="contact-message"
                      data-testid="contact-message"
                      value={form.message}
                      onChange={set("message")}
                      placeholder="Tell us what you need — the more detail, the faster we can help."
                      required
                      minLength={4}
                      maxLength={4000}
                      rows={6}
                    />
                    <div className="text-[11px] text-muted-foreground text-right">
                      {form.message.length} / 4000
                    </div>
                  </div>

                  <Button
                    type="submit"
                    data-testid="contact-submit-btn"
                    disabled={busy}
                    className="bg-[#C05A3C] hover:bg-[#A8492F] text-white w-full sm:w-auto"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                    Send message
                  </Button>
                  <p className="text-[11px] text-muted-foreground">
                    By sending you agree to our{" "}
                    <a href="/privacy" className="underline">privacy policy</a>. We never share your email.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
